import { setNonce } from "get-nonce";

/**
 * The document's style nonce (FR-015-151, 2026-09-29). The document's Content-Security-Policy admits
 * a `<style>` element only when it carries the nonce the `api` role drew for that response, which
 * the role writes into the document's `<meta property="csp-nonce">` as it serves it (the build
 * puts the meta there through Vite's `html.cspNonce`, `apps/web/vite.config.ts`). Every library
 * that makes a style element at run time takes it from here:
 *
 * - Radix's scroll lock (`react-remove-scroll` through `react-style-singleton`), which reads
 *   `get-nonce` when it writes its element, so it is set there once;
 * - Motion's `AnimatePresence` in `popLayout` mode, which reads the `nonce` of `MotionConfig`
 *   (`MotionBridge`);
 * - Radix Select's and ScrollArea's viewports, which render a `<style>` with the `nonce` prop
 *   their component passes;
 * - the email reader's frame (`apps/web/src/m365/emails/EmailHtml.tsx`), whose `srcdoc` document
 *   inherits this document's policy and so carries the nonce on its one `<style>`.
 *
 * Tiptap's element stays off (`injectCSS: false`), its rules being in `Editor.css`, so it needs
 * none. Each entry calls {@link adoptStyleNonce} first, before it renders anything that could
 * open an overlay.
 */
let adopted: string | undefined;

/**
 * Read the nonce from the document and hand it to `get-nonce`. A browser hides the `nonce`
 * attribute of an element once a header-delivered policy governs its document, leaving the value
 * on the `nonce` property alone, so the property is read first and the attribute only where
 * there is no such property (jsdom). A document with no meta, which is none this build serves,
 * leaves every library writing its elements without a nonce, which is what it did before.
 */
export function adoptStyleNonce(doc: Document = document): string | undefined {
  const meta = doc.querySelector<HTMLMetaElement>('meta[property="csp-nonce"]');
  const property = meta?.nonce ?? "";
  const value = property === "" ? (meta?.getAttribute("nonce") ?? "") : property;
  adopted = value === "" ? undefined : value;
  if (adopted !== undefined) setNonce(adopted);
  return adopted;
}

/** The `nonce` prop of a component that hands the nonce {@link adoptStyleNonce} read to a library itself, and no prop when there is none. */
export function styleNonceProps(): { readonly nonce?: string } {
  return adopted === undefined ? {} : { nonce: adopted };
}
